import { useEffect, useState, type ReactNode } from 'react';
import { Link, Redirect, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { Footer } from './Footer';
import { ScrollableTabStrip } from './ScrollableTabStrip';
import { api } from '../lib/api';
import type { AppUser } from '../types';
import { useAppSettings } from '../hooks/useAppSettings';
import { useFeature } from '../hooks/useFeature';
import { cn } from '../lib/utils';
import { LAYOUT } from '../styles/layout-tokens';

interface SettingsLayoutProps {
  children: ReactNode;
}

export function SettingsLayout({ children }: SettingsLayoutProps) {
  const { t } = useTranslation();
  const [user, setUser] = useState<AppUser | null | undefined>(undefined);
  const [location] = useLocation();
  const settings = useAppSettings();
  const backupsEnabled = useFeature('periodic_backup_email');
  const githubSyncEnabled = useFeature('github_sync');

  useEffect(() => {
    api.getMe().then(setUser).catch(() => setUser(null));
  }, []);

  const handleLogout = async () => {
    try {
      await api.logout();
      window.location.href = '/';
    } catch {
      toast.error(t('toasts.logoutFailed'));
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="text-sm text-zinc-500">…</div>
      </div>
    );
  }

  if (!user) return <Redirect to="/" replace />;

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className={cn(LAYOUT.container.wide, 'mx-auto', LAYOUT.page.paddingX, LAYOUT.header.height, 'flex items-center justify-between gap-3')}>
          <Link href="/home" className="flex items-center gap-3">
            <Logo className="h-7 w-auto text-zinc-900 dark:text-zinc-50" />
            <span className="font-bold text-lg tracking-tight brand-text">
              {t('app.name')}
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 border border-zinc-300 dark:border-zinc-700 rounded px-1.5 py-0.5">
              {t('settings.layout.badge')}
            </span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <UserMenu user={user} onLogout={handleLogout} />
          </div>
        </div>
        <ScrollableTabStrip>
          <SettingsTab
            href="/settings/balance"
            active={location.startsWith('/settings/balance')}
          >
            {t('settings.tabs.balance')}
          </SettingsTab>
          <SettingsTab
            href="/settings/api-keys"
            active={location.startsWith('/settings/api-keys')}
          >
            {t('settings.tabs.apiKeys')}
          </SettingsTab>
          {settings.categorization.enabled && (
            <SettingsTab
              href="/settings/categorization"
              active={location.startsWith('/settings/categorization')}
            >
              {t('settings.tabs.categorization')}
            </SettingsTab>
          )}
          <SettingsTab
            href="/settings/display"
            active={location.startsWith('/settings/display')}
          >
            {t('settings.tabs.display')}
          </SettingsTab>
          <SettingsTab
            href="/settings/resurface"
            active={location.startsWith('/settings/resurface')}
          >
            {t('settings.tabs.resurface')}
          </SettingsTab>
          {backupsEnabled && (
            <SettingsTab
              href="/settings/backups"
              active={location.startsWith('/settings/backups')}
            >
              {t('settings.tabs.backups')}
            </SettingsTab>
          )}
          {githubSyncEnabled && (
            <SettingsTab
              href="/settings/github-sync"
              active={location.startsWith('/settings/github-sync')}
            >
              {t('settings.tabs.githubSync')}
            </SettingsTab>
          )}
          <SettingsTab
            href="/settings/spaces"
            active={location.startsWith('/settings/spaces')}
          >
            {t('settings.tabs.spaces')}
          </SettingsTab>
          <SettingsTab
            href="/settings/account"
            active={location.startsWith('/settings/account')}
          >
            {t('settings.tabs.account')}
          </SettingsTab>
          <SettingsTab
            href="/settings/security"
            active={location.startsWith('/settings/security')}
          >
            {t('auth.security.title')}
          </SettingsTab>
        </ScrollableTabStrip>
      </header>

      <main className="flex-1">
        <div className={cn(LAYOUT.container.default, 'mx-auto', LAYOUT.page.paddingX, LAYOUT.page.paddingY)}>{children}</div>
      </main>

      <Footer />
    </div>
  );
}

function SettingsTab({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={cn(
        'inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 transition-colors shrink-0',
        active
          ? 'border-[var(--brand)] text-[var(--brand)]'
          : 'border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-zinc-300 dark:hover:border-zinc-700',
      )}
    >
      {children}
    </Link>
  );
}
